/**
 * Formats an ISO timestamp for a chat message/selection footer, in the same
 * three tiers the conversation list uses (`parseTime.ts`) — separated with a
 * comma rather than that column's "·", so a bubble reads the same way as the
 * date divider above it:
 *
 *   - today                → "5:07 PM"
 *   - earlier this year    → "Aug 18, 5:07 PM"
 *   - a different year     → "Aug 18, 2025, 5:07 PM"
 *
 * The footer used to be the clock alone, with no date at any age, so a
 * transcript spanning more than a year could not be read unambiguously: the
 * only absolute reference was a date divider that itself omitted the year
 * (PROD-773). At production scale, conversations older than a year exist and a
 * member has to be able to tell when a message landed.
 *
 * Returns "" for an invalid/empty input so callers can render conditionally
 * without guarding the parse themselves.
 */
export function formatTime(iso: string): string {
  const date = new Date(iso);
  if (isNaN(date.getTime())) return "";

  const now = new Date();
  const time = date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

  const isToday = date.getFullYear() === now.getFullYear() && date.getMonth() === now.getMonth() && date.getDate() === now.getDate();
  if (isToday) return time;

  const datePart = date.toLocaleDateString([], {
    month: "short",
    day: "numeric",
    // Only when it cannot be inferred — a same-year date reads fine without it.
    ...(date.getFullYear() !== now.getFullYear() ? { year: "numeric" } : {})
  });
  return `${datePart}, ${time}`;
}
